import api from "./api";
import { createFile, getFileUuidFromCreateResponse } from "./file";

/**
 * Fetch conversation history for a specific user and agent
 * @param {Object} params - Parameters for fetching conversations
 * @param {string} params.userId - The user UUID
 * @param {string} params.agentId - The agent UUID
 * @returns {Promise} Response with conversations list
 */
export const getConversationHistory = async ({ userId, agentId }) => {
  try {
    const payload = {
      userUUid: userId,
      agentUuid: agentId,
    };

    const response = await api.post('/v1/conversation/list', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching conversation history:', error);
    throw error;
  }
};

/**
 * Fetch messages for a specific conversation
 * @param {Object} params - Parameters for fetching messages
 * @param {string} params.conversationId - The conversation ID
 * @returns {Promise} Response with messages list
 */
export const getConversationMessages = async ({ conversationId }) => {
  try {
    const payload = { conversationUuid: conversationId };

    const response = await api.post('/v1/message/list', payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching conversation messages:", error);
    throw error;
  }
};

/**
 * Upload a file for use as a chat attachment. Returns the file UUID to pass to sendMessage as file_uuids.
 * Uses POST /v1/tenant/file/create (same flow as Knowledge/HomePage) then PUT file to the returned uploadUrl.
 * Body shape aligned with backend: name, folder_id, file_type, status, agent_uuid, conversation_uuid.
 * @param {File} file - The file to upload
 * @param {Object} [options] - { agentUuid, conversationUuid } (required for chat attachments)
 * @returns {Promise<{ fileUuid: string, name: string, fileType: string }>} File UUID and details for sendMessage and message display
 */
export const uploadChatAttachment = async (file, options = {}) => {
  if (!file || !(file instanceof File)) {
    throw new Error("uploadChatAttachment requires a File");
  }
  const { agentUuid, conversationUuid } = options;
  const name = file.name || "attachment";
  const fileType =
    (name.includes(".") ? name.split(".").pop() : "") || file.type || "text/plain";
  const createFileBody = {
    name,
    folder_id: "",
    file_type: file.type || "text/plain",
    status: "active",
    agent_uuid: agentUuid || "",
    conversation_uuid: conversationUuid || "",
  };

  const response = await createFile(createFileBody);
  const data = response.data;
  const body = data?.body ?? data;
  const uploadUrl = body?.uploadUrl ?? body?.upload_url;
  if (!uploadUrl) {
    throw new Error("Upload URL not returned from createFile");
  }

  const putResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.status}`);
  }

  const fileUuid = getFileUuidFromCreateResponse(body);
  if (!fileUuid) {
    console.warn(
      "createFile did not return file uuid; send-asynchronous file_uuids may not work.",
    );
  }
  return { fileUuid, name, fileType };
};

/**
 * Send a message in a conversation (async; backend may stream or poll for response).
 * @param {Object} params - Parameters for sending
 * @param {string} params.messageText - Message text
 * @param {string} params.conversationUuid - Conversation UUID
 * @param {string} params.agentUuid - Agent (assistant) UUID
 * @param {string} [params.channel] - Channel (default 'WEB')
 * @param {string[]} [params.fileUuids] - Optional list of file UUIDs (from uploadChatAttachment) to attach
 * @param {Object} [params.llmConfig] - Optional LLM config { llm_model, llm_model_provider } for model selection
 * @param {Object} [params.context] - Injected context (location, entity, entityValue, scope) — Story 7. scope va dentro de context.
 * @returns {Promise} Response from send operation
 */
export const sendMessage = async ({
  messageText,
  conversationUuid,
  agentUuid,
  channel = "WEB",
  fileUuids,
  llmConfig,
  context,
}) => {
  try {
    const payload = {
      message_text: messageText,
      assistant_uuid: agentUuid ?? "",
      conversation_uuid: conversationUuid ?? "",
      channel: channel ?? "WEB",
    };

    if (fileUuids && Array.isArray(fileUuids) && fileUuids.length > 0) {
      payload.file_uuids = fileUuids;
    }

    if (llmConfig && typeof llmConfig === "object") {
      payload.llm_config = llmConfig;
    }

    if (context && typeof context === 'object' && Object.keys(context).length > 0) {
      payload.context = context; // single object per architect spec (scope, location, ref_entity, ref_entity_uuid, user_name, timezone)
    }

    const response = await api.post('/v1/message/send-asynchronous', payload);
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Delete a conversation
 * @param {string} conversationId - The conversation ID to delete
 * @returns {Promise} Response from delete operation
 */
export const deleteConversation = async (conversationId) => {
  try {
    const response = await api.delete(`/conversation/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting conversation:", error);
    throw error;
  }
};

/**
 * Create a new conversation
 * @param {Object} params - Parameters for creating conversation
 * @param {string} params.assistantUuid - The assistant (agent) UUID
 * @param {string} [params.title] - Optional title for the conversation
 * @returns {Promise} Response with created conversation data
 */
export const createConversation = async ({ assistantUuid, title }) => {
  try {
    const payload = {
      assistant_uuid: assistantUuid,
    };

    if (title) {
      payload.title = title;
    }

    // Use axios api.post - it handles headers (Content-Type, Authorization) automatically
    // Endpoint: /v1/workspace/conversation/create
    const response = await api.post('/v1/workspace/conversation/create', payload);

    // Axios automatically parses JSON response into response.data
    return response.data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    
    // Handle axios errors - error.response.data contains the API error response
    if (error?.response?.data) {
      const apiError = new Error(
        error.response.data.message || 
        error.response.data.msg_code?.[0]?.description || 
        error.message
      );
      apiError.status_code = error.response.data.status_code || error.response.status;
      apiError.msg_code = error.response.data.msg_code || [];
      throw apiError;
    }
    
    throw error;
  }
};

/**
 * Update conversation metadata (e.g., title)
 * @param {string} conversationId - The conversation ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise} Response from update operation
 */
export const updateConversation = async (conversationId, updates) => {
  try {
    const response = await api.patch(`/conversation/${conversationId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating conversation:', error);
    throw error;
  }
};

